// frontend/src/components/entry/StoreClaimQuestion.jsx
//
// The one question a Shopify sign-in can leave open, as a popup in front
// of the app (`entryScreen.js` puts it first).
//
// Two questions, both from the claim part of `GET /api/store/entry/`
// (backend/shopify_app/services/install.py pending_question):
//
//   add_or_create — the merchant already belongs to an organization and the
//                   store is unclaimed: pick which organization takes it, or
//                   create a new one. The merchant names the new one; the
//                   field starts with the shop's name as a suggestion.
//   confirm_join  — the store belongs to someone else's organization and the
//                   merchant has their own: confirm joining it as a member.
//
// The store is never named by this component: the backend answers for the
// store the sign-in token named and nothing else. On success the session
// has selected that store, and the document restarts into it the way a
// store switch does (`AuthContext.switchStore`).
//
// A stale question (the store was claimed by a colleague meanwhile) comes
// back as `answer_mismatch` / `store_already_claimed`; both re-read the
// question, which then reports nothing pending and the app opens.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, FormControlLabel, Radio, RadioGroup, TextField, Typography,
} from '@mui/material';

import { useAuth } from '../../contexts/AuthContext';
import { translateRequestError } from '../../queries/api';
import EntryDialog from './EntryDialog';

const NEW_ORGANIZATION = '__new__';

// Structured codes the answer can come back with
// (shopify_app.services.install.InstallError, shopify_app.views.saas), each
// with its own copy. Anything else reads as the generic failure — with the
// code, so support has something to quote.
const ERROR_KEYS = new Set([
  'account_deactivated', 'not_an_org_member', 'organization_not_found',
  'invalid_org_name', 'org_name_too_long',
]);
// A stale question is answered by re-reading it, never by a sentence.
const RE_READ_CODES = new Set(['answer_mismatch', 'store_already_claimed', 'no_pending_question']);

function describeError(t, err) {
  const code = err?.code ?? null;
  if (code && ERROR_KEYS.has(code)) return t(`storeClaim.errors.${code}`);
  if (code) return t('storeClaim.errors.unknownCode', { code });
  return translateRequestError(err, t, 'storeClaim.errors.generic');
}

export default function StoreClaimQuestion({ question, onAnswer, onReread }) {
  const { t } = useTranslation();
  const { switchStore } = useAuth();
  const isAddOrCreate = question.question === 'add_or_create';
  const storeName = question.store.name;
  // The payload carries `organizations` for add_or_create only (the user's
  // own, never empty — the phase needs one); confirm_join names the one
  // organization to join instead.
  const organizations = isAddOrCreate
    ? [...question.organizations].sort(
      (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
    )
    : [];
  const [choice, setChoice] = useState(isAddOrCreate ? organizations[0].id : NEW_ORGANIZATION);
  const [newName, setNewName] = useState(storeName);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    const body = !isAddOrCreate
      ? { answer: 'confirm' }
      : choice === NEW_ORGANIZATION
        ? { answer: 'create', organization_name: newName }
        : { answer: 'add', organization_id: choice };
    let store;
    try {
      ({ selected_store: store } = await onAnswer(body));
    } catch (err) {
      console.error('Store claim answer failed:', err?.message || err);
      if (RE_READ_CODES.has(err?.code)) {
        onReread();
        return;
      }
      setError(describeError(t, err));
      setSubmitting(false);
      return;
    }
    // The claim is committed: a switch that fails here is not a failed setup,
    // and the question must not be offered again. The entry read puts the
    // merchant into the store.
    try {
      await switchStore(store.id, () => '/');
    } catch (err) {
      console.error('Store claimed, but the switch into it failed:', err?.message || err);
      onReread();
    }
  };

  return (
    <EntryDialog
      title={isAddOrCreate ? t('storeClaim.addOrCreate.title') : t('storeClaim.confirmJoin.title')}
      actions={(
        <Button variant="contained" onClick={submit} loading={submitting} disabled={submitting}>
          {isAddOrCreate ? t('storeClaim.addOrCreate.continue') : t('storeClaim.confirmJoin.join')}
        </Button>
      )}
    >
      {isAddOrCreate ? (
        <>
          <Typography color="text.secondary">
            {t('storeClaim.addOrCreate.body')}
          </Typography>
          <RadioGroup
            aria-label={t('storeClaim.addOrCreate.title')}
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
          >
            {organizations.map((org) => (
              <FormControlLabel
                key={org.id}
                value={org.id}
                control={<Radio />}
                label={t('storeClaim.addOrCreate.addTo', { org: org.name })}
              />
            ))}
            <FormControlLabel
              value={NEW_ORGANIZATION}
              control={<Radio />}
              label={t('storeClaim.addOrCreate.createNew')}
            />
          </RadioGroup>
          {choice === NEW_ORGANIZATION && (
            <TextField
              label={t('storeClaim.addOrCreate.newName')}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
              fullWidth
            />
          )}
        </>
      ) : (
        <Typography color="text.secondary">
          {t('storeClaim.confirmJoin.body', {
            store: storeName, org: question.organization.name,
          })}
        </Typography>
      )}

      {error && <Alert severity="error">{error}</Alert>}
    </EntryDialog>
  );
}
